import { FC, FocusEvent, MouseEvent, PropsWithChildren, ReactNode, useCallback, useEffect, useRef } from "react";

import classNames from "classnames";

import { BaseProps } from "../../../BaseProps";
import { Icon, TooltipStyle } from "../../../general";
import { Shelf } from "../../../layout";
import { createComponentStyleHook } from "../../../tokens";
import { Body, Bold, Regular, Semibold } from "../../../typography";
import { getBasePropsFromHtmlProps } from "../../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../../utilities/getHtmlPropsFromBaseProps";
import { getStyleModeSafeProps } from "../../utils/getStyleModeSafeProps";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

/**
 * The `Label` component properties.
 */
export interface LabelProps extends BaseProps {
  /**
   * The ID of the component the label is for.
   */
  readonly forId?: string;

  /**
   * The size of the label text. If not specified defaults to `"mini".
   */
  readonly size?: "mini" | "small" | "medium" | "inherit";

  /**
   * Indicates the appearance of the label. If not specified defaults to "semibold".
   */
  readonly appearance?: "bold" | "semibold" | "regular";

  /**
   * Indicates whether the input the label is for is required.
   */
  readonly required?: boolean;

  /**
   * The tooltip for the element.
   */
  readonly tooltip?: ReactNode;
}

const useResolvedLabelStyles = createComponentStyleHook<LabelProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "form_label",
});

/**
 * An input component label.
 */
export const Label: FC<PropsWithChildren<LabelProps>> = (props) => {
  const { forId, size = "mini", appearance = "semibold", required = false, tooltip, children } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedLabelStyles(getStyleModeSafeProps(props));

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Label, "label"],
    testIdPrefix: "label",
  });

  const forIdRef = useRef<string | undefined>(forId);
  useEffect(() => {
    forIdRef.current = forId;
  }, [forId]);

  const focusHandler = useCallback((ev: FocusEvent) => {
    ev.preventDefault();
  }, []);

  const mouseDownHandler = useCallback((ev: MouseEvent) => {
    const { current: forId } = forIdRef;
    const { button } = ev;

    if (button !== 0) return;
    if (forId === undefined) return;

    const target = document.getElementById(forId);
    target?.focus();

    ev.preventDefault();
  }, []);

  const mouseEnterHandler = useCallback(() => {
    const { current: forId } = forIdRef;
    if (forId === undefined) return;

    const target = document.getElementById(forId);
    const event = new CustomEvent("cust_labelMouseEnter", { bubbles: true, cancelable: true });
    target?.dispatchEvent(event);
  }, []);

  const mouseLeaveHandler = useCallback(() => {
    const { current: forId } = forIdRef;
    if (forId === undefined) return;

    const target = document.getElementById(forId);
    const event = new CustomEvent("cust_labelMouseExit", { bubbles: true, cancelable: true });
    target?.dispatchEvent(event);
  }, []);

  return (
    <StyleModeBoundary mode={mode}>
      <Body {...getBasePropsFromHtmlProps(baseHtmlProps)} inline size={size}>
        <label
          id={forId === undefined ? undefined : `${forId}-label`}
          htmlFor={forId}
          onFocus={focusHandler}
          onMouseDown={mouseDownHandler}
          onMouseEnter={mouseEnterHandler}
          onMouseLeave={mouseLeaveHandler}
        >
          <Shelf inline spacing="mini">
            {appearance === "regular" && (
              <Regular>
                {children}
                {required && children && (
                  <>
                    &nbsp;<Semibold>*</Semibold>
                  </>
                )}
              </Regular>
            )}
            {appearance === "semibold" && (
              <Semibold>
                {children}
                {required && children && <>&nbsp;*</>}
              </Semibold>
            )}
            {appearance === "bold" && (
              <Bold>
                {children}
                {required && children && (
                  <>
                    &nbsp;<Semibold>*</Semibold>
                  </>
                )}
              </Bold>
            )}
            {tooltip && (
              <TooltipStyle alignment="top">
                <Icon className={classNames(styles.Icon, "tooltip-icon")} name="info-circle" size="small" color="body" tooltip={tooltip} />
              </TooltipStyle>
            )}
            <Shelf.Spacer />
          </Shelf>
        </label>
      </Body>
    </StyleModeBoundary>
  );
};
